/**
 * 品牌列表页面 — BrandList。
 *
 * GET /api/brands 获取列表（可按公司筛选）；GET /api/companies 解析所属公司名。
 * 从全局 companyStore 读取 selectedCompanyId 做筛选。
 * DataTable 展示：品牌名 / 所属公司 / 品牌定位 / 品牌口号 / 目标人群。
 * 点击行跳转 /brands/{id}。
 * 只读呈现（PRD 1.2 工作流：前端只读，skill 主录入）。
 *
 * TODO(概念稿待后端补字段): 产品数、AI 可见性均值+环比、监测话题数、
 * 最近探测时间 —— 现有 GET /api/brands 未返回，
 * 待接口补充后按概念稿 brands.html 信息架构补齐列。
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  TextField,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import Loading from "../components/Loading";
import { getBrands } from "../api/brands";
import { getCompanies } from "../api/companies";
import { useCompanyStore } from "../store/companyStore";
import type { Brand } from "../types";

export default function BrandList() {
  const navigate = useNavigate();
  const selectedCompanyId = useCompanyStore((s) => s.selectedCompanyId);
  const [data, setData] = useState<Brand[]>([]);
  const [companyNames, setCompanyNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    setLoading(true);
    setError("");
    getBrands(selectedCompanyId || undefined)
      .then(setData)
      .catch((err) =>
        setError(err instanceof Error ? err.message : "获取品牌列表失败"),
      )
      .finally(() => setLoading(false));
  }, [selectedCompanyId]);

  // 所属公司名映射（company_id → name，复用现有 GET /api/companies）
  useEffect(() => {
    getCompanies()
      .then((companies) => {
        const names: Record<string, string> = {};
        for (const c of companies) {
          names[c.id] = c.name;
        }
        setCompanyNames(names);
      })
      .catch(() => setCompanyNames({}));
  }, []);

  const columns: DataTableColumn<Brand>[] = [
    {
      key: "name",
      label: "品牌名称",
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "primary.main" }}>
            {row.name}
          </Typography>
          {row.full_name && row.full_name !== row.name && (
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              {row.full_name}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: "company",
      label: "所属公司",
      render: (row) => (
        <Typography sx={{ fontSize: 13 }}>
          {companyNames[row.company_id] || "—"}
        </Typography>
      ),
    },
    {
      key: "brand_positioning",
      label: "品牌定位",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.brand_positioning || "—"}
        </Typography>
      ),
    },
    {
      key: "brand_slogan",
      label: "品牌口号",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.brand_slogan || "—"}
        </Typography>
      ),
    },
    {
      key: "target_audience",
      label: "目标人群",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.target_audience || "—"}
        </Typography>
      ),
    },
  ];

  const filteredItems = data.filter((item) => {
    if (!searchText) return true;
    const text = searchText.toLowerCase();
    return (
      item.name.toLowerCase().includes(text) ||
      (item.full_name || "").toLowerCase().includes(text) ||
      (item.brand_positioning || "").toLowerCase().includes(text)
    );
  });

  return (
    <Box>
      <PageHeader
        title="品牌"
        breadcrumbs={[{ label: "资产管理" }, { label: "品牌" }]}
      />

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="搜索品牌名称、定位..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchOutlined
                      sx={{ color: "text.secondary", fontSize: 18, mr: 1 }}
                    />
                  ),
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Loading fullPage text="正在加载品牌列表..." />
      ) : error ? (
        <Card>
          <CardContent>
            <Typography sx={{ fontSize: 13, color: "error.main" }}>
              {error}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          data={filteredItems}
          total={filteredItems.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(size) => {
            setRowsPerPage(size);
            setPage(0);
          }}
          onRowClick={(row) => navigate(`/brands/${row.id}`)}
          getRowId={(row) => row.id}
          emptyText="暂无品牌数据"
        />
      )}
    </Box>
  );
}
